import { FormItem } from "../../infinity-item/interfaces/FormItem.interface";
import { FormRule } from "../interfaces/FormRule.interface";
import { Store } from "../../infinity-store/interfaces/Store.interface";
import { addRule } from "./addRule";
import { copy } from "../../infinity-util/methods/copy";
import { getValue } from "../../infinity-store/methods/getValue";
import { send } from "../../infinity-store/methods/send";
import { itemReducer } from "../../infinity-item/reducers/itemReducer";
import { ruleReducer } from "../reducers/ruleReducer";

interface InitItemConfig {
    parent?: FormItem,
    append?: boolean,
    prepend?: boolean
}

export function initItem(formItem: FormItem, store: Store, config: InitItemConfig = {}): FormItem {
    // Alias config props.
    const { parent, append, prepend } = copy(config, { parent: {}, append: false, prepend: false }, { notExisting: true });

    // Get new items.
    const newItems = itemReducer([], formItem);

    // Update items with default values and rules.
    const defaults = {
        visible: true,
        disabled: false,
        valid: false,
        placeholder: ""
    };
    newItems.forEach(item => {
        // Copy default values without overwriting.
        copy(item, defaults, { notExisting: true });
        if (!item.rules) item.rules = {};

        // Alias item properties.
        const { type, rules, itemId, parentItemId, items } = item;

        // Default rules.
        const hasValue: FormRule = { compare: [`#${itemId}.value`, "!!"] };
        const parentHasValue: FormRule = { compare: [`#${parentItemId}.value`, "==", itemId] };

        // Validation.
        if (item.required && ["aggregate", "none", "submit", "checkboxGroup"].indexOf(type) < 0) {
            addRule(rules, itemId, `${itemId}.valid`, hasValue);
        }

        // Modal visibility.
        if (type === "modal") addRule(rules, itemId, `${itemId}.visible`, hasValue);

        // Checkbox group validation (currently only min selection 1).
        if (type === "checkboxGroup") {
            const minSelection: FormRule = { any: items.map(child => (<FormRule>{ compare: [`#${child.itemId}.value`, "==", true] })) };
            items.forEach(child => addRule(rules, child.itemId, `${itemId}.valid`, minSelection));
        }

        // Input child visibility.
        if (items && items.length && ["text", "textarea", "date", "email", "number", "checkbox"].indexOf(type) > -1) {
            items.forEach(child => {
                if (child.className && child.className.indexOf("check-na") > -1) return;
                addRule(rules, itemId, `${child.itemId}.visible`, hasValue);
            });
        }

        // Radio validation & child visibility.
        if (items && items.length && type === "radio") {
            addRule(rules, parentItemId, `${itemId}.valid`, parentHasValue);
            items.forEach(child => addRule(rules, parentItemId, `${child.itemId}.visible`, parentHasValue));
        }
    });

    // Add rules to global rules.
    const rules = newItems.reduce(ruleReducer, store.rules || {});
    send({ rules }, { store });

    // Batch updates.
    const updates = newItems.reduce((obj: any, item: FormItem) => (!item || !item.itemId) ? obj : (obj[item.itemId] = item, obj), {});  // Add new form items.
    Object.keys(rules).forEach(id => {
        if (updates[id] === undefined) updates[id] = getValue(id, store); // Add all watched values.
    });
    send(updates, { store, batch: true });

    // Add item to parent.
    if (append) parent.items.push(formItem);
    if (prepend) parent.items.unshift(formItem);

    return formItem;
}
